import { type HTMLMotionProps, motion } from 'framer-motion';
import * as React from 'react';
import { cardHoverVariants, prefersReducedMotion, springSnappy } from '@/lib/motion';
import { cn } from '@/lib/utils';

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Enable hover lift effect for interactive cards */
  interactive?: boolean;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, interactive, children, onClick, ...props }, ref) => {
    const shouldAnimate = interactive && !prefersReducedMotion();

    if (shouldAnimate) {
      return (
        <motion.div
          ref={ref}
          className={cn(
            'rounded-radius-lg border border-border bg-card text-card-foreground shadow',
            className,
          )}
          variants={cardHoverVariants}
          initial="initial"
          whileHover="hover"
          whileTap="tap"
          transition={springSnappy}
          onClick={onClick}
        >
          {children}
        </motion.div>
      );
    }

    return (
      <div
        ref={ref}
        className={cn(
          'rounded-radius-lg border border-border bg-card text-card-foreground shadow',
          'transition-shadow hover:shadow-md',
          className,
        )}
        onClick={onClick}
        {...props}
      >
        {children}
      </div>
    );
  },
);
Card.displayName = 'Card';

/**
 * Motion-enhanced Card with hover lift effect.
 * Perfect for clickable cards, project tiles, and interactive list items.
 */
export interface MotionCardProps extends Omit<HTMLMotionProps<'div'>, 'ref'> {
  /** Disable motion animations */
  disableMotion?: boolean;
}

const MotionCard = React.forwardRef<HTMLDivElement, MotionCardProps>(
  ({ className, disableMotion, ...props }, ref) => {
    const shouldAnimate = !(disableMotion || prefersReducedMotion());

    return (
      <motion.div
        ref={ref}
        className={cn(
          'rounded-radius-lg border border-border bg-card text-card-foreground shadow cursor-pointer',
          className,
        )}
        variants={shouldAnimate ? cardHoverVariants : undefined}
        initial="initial"
        whileHover={shouldAnimate ? 'hover' : undefined}
        whileTap={shouldAnimate ? 'tap' : undefined}
        transition={springSnappy}
        {...props}
      />
    );
  },
);
MotionCard.displayName = 'MotionCard';

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col space-y-1.5 p-6', className)} {...props} />
  ),
);
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn('font-semibold leading-none tracking-tight', className)}
      {...props}
    />
  ),
);
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
));
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('p-6 pt-0', className)} {...props} />
  ),
);
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center p-6 pt-0', className)} {...props} />
  ),
);
CardFooter.displayName = 'CardFooter';

export { Card, MotionCard, CardHeader, CardFooter, CardTitle, CardDescription, CardContent };
